import { usePermissions } from "@/Composables";
import { hasRouteValue } from "@/Composables/useRouteSupport";
import {
  BookOpenIcon,
  CalculatorIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  HomeIcon,
  MapPinIcon,
  MegaphoneIcon,
  PaperAirplaneIcon,
  RectangleStackIcon,
  SquaresPlusIcon,
  UserGroupIcon,
} from "@heroicons/vue/24/outline";
import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";

export default function memberNavigation() {
  const page = usePage();
  const user = computed(() => page.props?.auth?.user);
  const orderBuilderTool = computed(() => page.props?.orderBuilderTool);
  const summitEventId = computed(() => page.props?.summit?.id ?? null);
  const { can } = usePermissions();

  const navigation = [
    {
      name: "Home",
      route: "dashboard.index",
      icon: HomeIcon,
      component: "Member/Dashboard",
    },
    {
      name: "Blue Book",
      route: "blue-book-platform.index",
      icon: BookOpenIcon,
      children: [
        { name: "View Blue Book", route: "blue-book-platform.index" },
        {
          name: "Blue Book Supplier Programs Report",
          route: "blue-book-platform.reports.programs.index",
          component: "BlueBookPlatform/Reports/Programs/Index",
        },
        {
          name: "Supplier Closeouts Report",
          route: "blue-book-platform.reports.current-closeouts.index",
          component: "BlueBookPlatform/Reports/CurrentCloseouts/Index",
        },
        {
          name: "Blue Book/Price List Updates Report",
          href: route("blue-book-platform.recent-updates.index"),
          // component: "Member/Invoices",
          target: "_blank",
        },
      ],
    },
    ...(can("list claims")
      ? [
          {
            name: "Accounting",
            icon: CalculatorIcon,
            children: [
              { name: "Invoices", route: "invoices.index", component: "Member/Invoices" },
              {
                name: "Invoice Research",
                route: "invoice-research.index",
                component: "Member/InvoiceResearches",
              },
              { name: "Claims", route: "claims.index", component: "Claims/Index" },
            ],
          },
        ]
      : []),
    {
      name: "Midco Catalog/Reports",
      icon: BookOpenIcon,
      href: route("shopify-sso.idp"),
      target: "_blank",
      external: true,
    },
    {
      name: "Calendar of Events",
      route: "upcoming-events.index",
      component: "UpcomingEvents/Index",
      icon: CalendarIcon,
    },
    {
      name: "Contacts",
      icon: UserGroupIcon,
      children: [
        {
          name: "MSD Board Members",
          route: "contacts.msd-board-members.index",
          component: "Contacts/MsdBoardMembers",
        },
        {
          name: "MSD Contacts",
          route: "contacts.msd-contacts.index",
          component: "Contacts/MsdContacts",
        },
        {
          name: "Member Companies",
          route: "contacts.member-companies.index",
          component: "Contacts/MemberCompanies",
        },
        {
          name: "Suppliers",
          route: "contacts.suppliers.index",
          component: "Contacts/Suppliers",
        },
        {
          name: "Rep Groups",
          route: "contacts.reps.index",
          component: "Contacts/Reps",
        },
        {
          name: "My Company Profile/Contacts",
          route: "company-profile.index",
          component: "CompanyProfile/Index",
        },
      ],
    },
    {
      name: "Trade Shows",
      icon: CurrencyDollarIcon,
      children: [
        {
          name: "Upcoming/Next Shows",
          route: "events.next-shows.index",
          component: "Events/NextShows/Index",
        },
        {
          name: "Archived Shows",
          route: "events.archived-shows.index",
          component: "Events/ArchivedShows/Index",
        },
      ],
    },
    ...(hasRouteValue(summitEventId.value)
      ? [
          {
            name: "Summit",
            icon: CurrencyDollarIcon,
            route: "events.event.summit.registration",
            params: { event: summitEventId.value, company_id: user.value?.company_id },
            component: "Events/Summit/Registration",
          },
        ]
      : []),
    { name: "Committees", route: "committees.index", icon: RectangleStackIcon, component: "Committees/Index" },
    {
      name: "MSD Communications",
      route: "communications.sent.index",
      icon: MegaphoneIcon,
      component: "InLook",
    },
    { name: "Member Map", route: "locations.index", icon: MapPinIcon, component: "Locations/Index" },
    {
      name: "Central Services",
      defaultOpen: true,
      icon: SquaresPlusIcon,
      children: [
        { name: "3E", href: "https://3eco.com", target: "_blank", external: true },
        { name: "Weather Trends", href: "https://weathertrends360.com", target: "_blank", external: true },
        { name: "National Shooting Sports", href: "https://www.nssf.org", target: "_blank", external: true },
      ],
    },
    {
      name: "Committee Travel Request Form",
      route: "travel.requests.index",
      icon: PaperAirplaneIcon,
      component: "Travel/Requests/Index",
    },
    // This is being taken care of in the shopify sso.idp route, so we don't need to show it here anymore
    // {
    //   name: "Warehouse Programs Information",
    //   icon: TruckIcon,
    //   children: [
    //     // { name: "Catalog", route: "warehouse.catalog.index", component: "Warehouse/Catalog/Index" },
    //     // { name: "Reports", route: "warehouse.reports.index", component: "Warehouse/Reports/Index" },
    //     {
    //       name: "Private Brands",
    //       route: "warehouse.private-brands.index",
    //       component: "Warehouse/PrivateBrands/Index",
    //     },
    //     {
    //       name: "Marketing Assets",
    //       route: "warehouse.marketing-assets.index",
    //       component: "Warehouse/MarketingAssets/Index",
    //     },
    //     { name: "Item Master", route: "warehouse.item-master.index", component: "Warehouse/ItemMaster/Index" },
    //     {
    //       name: "Forecast Database",
    //       route: "forecasting.index",
    //       component: "Forecasting/Index",
    //     },
    //     { name: "Defective Form", route: "warehouse.defective-form.index", download: true },
    //     // { name: "Media Valet", route: "warehouse.media-valet.index", component: "Warehouse/MediaValet/Index" },
    //     {
    //       name: "Domo Item & Inv. Info",
    //       href: "https://msdist.domo.com/app-studio/19408487/pages/299929680",
    //       target: "_blank",
    //       external: true,
    //     },
    //     {
    //       name: "Member Sales Opportunity Report (GAP)",
    //       href: "https://msdist.domo.com/app-studio/1124781699/pages/1214492197",
    //       target: "_blank",
    //       external: true,
    //     },
    //     {
    //       name: "Member Performance (POS)",
    //       href: "https://msdist.domo.com/app-studio/1313772669/pages/1982291290",
    //       target: "_blank",
    //       external: true,
    //     },
    //     // {
    //     //   name: "Stage Gate Projects",
    //     //   route: "stage-gate-projects.index",
    //     //   component: "StageGateProjects",
    //     //   class: "nav-stage-gate-projects",
    //     // }

    //     ...(orderBuilderTool
    //       ? [
    //           {
    //             name: "Order Builder Tool",
    //             href: orderBuilderTool.value,
    //             target: "_blank",
    //             external: true,
    //           },
    //         ]
    //       : []),
    //     // { name: "Midco Catalog", href: route("shopify-sso.idp"), target: "_blank", external: true },
    //   ],
    // },
    // {
    //   name: "Reports",
    //   icon: ChartBarIcon,
    //   children: [
    //     {
    //       name: "Current Closeouts",
    //       route: "blue-book-platform.reports.current-closeouts.index",
    //       component: "BlueBookPlatform/Reports/CurrentCloseouts/Index",
    //     },
    //     {
    //       name: "Recent Blue Book/Price List Updates",
    //       href: route("blue-book-platform.recent-updates.index"),
    //       // component: "Member/Invoices",
    //       target: "_blank",
    //     },
    //     {
    //       name: "Blue Book Program List",
    //       route: "blue-book-platform.reports.programs.index",
    //       component: "BlueBookPlatform/Reports/Programs/Index",
    //     },
    //   ],
    // },
    // { name: "Contacts", route: "contacts.index", icon: UserGroupIcon, component: "Contacts" },

    // { name: "Messages", action: () => state.toggleMessages(true), icon: ChatBubbleBottomCenterTextIcon },
    // {
    //   name: "Events",
    //   icon: CalendarIcon,
    //   children: [
    //     { name: "Event Calendar", route: "upcoming-events.index", component: "UpcomingEvents/Index" },
    //     // { name: "Event Registration", route: "events.registration.index", component: "Events/Registration/Index" },
    //     // { name: "Virtual Shows - Active", route: "events.index", component: "Events/Index" },
    //     // {
    //     //   name: "Virtual Shows - Archived",
    //     //   route: "events.archived.index",
    //     //   component: "Events/Archived/Index",
    //     // },

    //     { name: "Virtual Shows", route: "events.index", component: "Events" },
    //   ],
    // },

    // { name: "Reports", route: "reports.index", icon: ChartBarIcon, component: "Reports/Index" },
  ];

  return {
    navigation,
  };
}
